import { useEffect, useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import type { DashboardEmbed } from "@/api/types";
import { cn } from "@/lib/utils";
import { useDashboards } from "@/hooks/useDashboards";
import RebootAgenciasPage, { type ClienteReboot } from "@/pages/RebootAgenciasPage";
import RebootPanelesPage from "@/pages/RebootPanelesPage";

// Los tableros con control de reinicio DENTRO de la misma pantalla (no abre
// otra): un boton alterna entre el tablero embebido y la vista de reinicio,
// con "Volver al tablero" para regresar. Cada uno ve sólo los equipos de su
// cliente. `paneles` cambia la vista de routers por la de paneles de acceso.
const TABLEROS_CON_REINICIO: Record<
  string,
  { cliente: ClienteReboot; boton: string; paneles?: boolean }
> = {
  "aroc-nube-holanda-agencias": { cliente: "Holanda", boton: "Reiniciar agencias" },
  "aroc-nube-rutelas-tilatina": { cliente: "tiLatina", boton: "Reiniciar rutelas" },
  "aroc-nube-control-acceso-holanda": {
    cliente: "Holanda",
    boton: "Reiniciar paneles",
    paneles: true,
  },
};

// Etiquetas legibles para las categorías que devuelve el backend.
// Si llega una categoría no listada, se muestra capitalizando la primera letra.
const CATEGORY_LABELS: Record<string, string> = {
  general: "RED tiLatina",
  network: "Praxair",
  holanda: "NOVA",
  ips: "IPS",
};

function categoryLabel(slug: string): string {
  return CATEGORY_LABELS[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1);
}

// Factor de zoom del dashboard embebido. <1 = alejar: el iframe se renderiza
// a un viewport ampliado (1/ZOOM) y se reescala para llenar el contenedor, así
// Grafana maqueta sus paneles más chicos y cabe más contenido en pantalla.
// Ajustable en un solo lugar si se quiere afinar el nivel de acercamiento.
const DASHBOARD_ZOOM = 0.6;

// Agrupa los dashboards por categoría preservando el orden de aparición
// del backend. El orden de las categorías = orden del primer dashboard
// de cada categoría en la lista original.
function groupByCategory(
  items: DashboardEmbed[],
): Array<{ category: string; items: DashboardEmbed[] }> {
  const order: string[] = [];
  const buckets = new Map<string, DashboardEmbed[]>();
  for (const it of items) {
    if (!buckets.has(it.category)) {
      buckets.set(it.category, []);
      order.push(it.category);
    }
    buckets.get(it.category)!.push(it);
  }
  return order.map((cat) => ({ category: cat, items: buckets.get(cat)! }));
}

export function DashboardsPage() {
  const { data, isPending, error } = useDashboards();
  const items = data?.items ?? [];

  const groups = useMemo(() => groupByCategory(items), [items]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [showReboot, setShowReboot] = useState(false);

  // Categoría activa: la primera disponible. Si la actual desaparece, reset.
  useEffect(() => {
    if (groups.length === 0) {
      setActiveCategory(null);
      return;
    }
    if (!activeCategory || !groups.some((g) => g.category === activeCategory)) {
      setActiveCategory(groups[0].category);
    }
  }, [groups, activeCategory]);

  // Dashboard activo: el primero de la categoría activa. Si la categoría
  // cambia o el activo ya no pertenece, reset al primero del grupo.
  useEffect(() => {
    if (!activeCategory) {
      setActiveId(null);
      return;
    }
    const group = groups.find((g) => g.category === activeCategory);
    if (!group || group.items.length === 0) {
      setActiveId(null);
      return;
    }
    if (!activeId || !group.items.some((it) => it.id === activeId)) {
      setActiveId(group.items[0].id);
    }
  }, [groups, activeCategory, activeId]);

  // Reset del loading visual del iframe y de la vista de reinicio al cambiar
  // de dashboard: cambiar de pestaña siempre vuelve al tablero.
  useEffect(() => {
    setIframeReady(false);
    setShowReboot(false);
  }, [activeId]);

  const activeGroup = groups.find((g) => g.category === activeCategory) ?? null;
  const active = activeGroup?.items.find((it) => it.id === activeId) ?? null;
  const reinicio = active ? TABLEROS_CON_REINICIO[active.id] : undefined;
  const rebootView = !!reinicio && showReboot;
  const errorMessage = error
    ? error instanceof ApiError
      ? error.message
      : "Error al cargar la lista de dashboards."
    : null;

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboards</h1>
        {/* Sin mencionar el motor que sirve las gráficas ni mandar al operador
            fuera de AROC a editarlas: es la misma razón por la que se quitaron
            el enlace y la nota de vista embebida. */}
        <p className="text-sm text-muted-foreground">
          Métricas y gráficas operativas en tiempo real. Vista de solo lectura.
        </p>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      {isPending && !data ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Grafana no está configurado en este despliegue. Define{" "}
          <code className="rounded bg-muted px-1 py-0.5">AROC_GRAFANA_URL</code>{" "}
          para habilitar la sección.
        </div>
      ) : (
        <>
          {/* Nivel 1: pills de categorías. Solo se muestra si hay más de una. */}
          {groups.length > 1 && (
            <div
              role="tablist"
              aria-label="Categorías de dashboards"
              className="flex flex-wrap gap-2"
            >
              {groups.map((g) => {
                const isActive = g.category === activeCategory;
                return (
                  <button
                    key={g.category}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`dashboard-category-${g.category}`}
                    id={`dashboard-category-tab-${g.category}`}
                    onClick={() => setActiveCategory(g.category)}
                    className={cn(
                      "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                      isActive
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    {categoryLabel(g.category)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Nivel 2: tabs por dashboard dentro de la categoría activa. */}
          {activeGroup && (
            <div
              role="tablist"
              aria-label={`Dashboards de ${categoryLabel(activeGroup.category)}`}
              id={`dashboard-category-${activeGroup.category}`}
              className="flex flex-wrap gap-1 border-b"
            >
              {activeGroup.items.map((it) => {
                const isActive = it.id === activeId;
                return (
                  <button
                    key={it.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`dashboard-panel-${it.id}`}
                    id={`dashboard-tab-${it.id}`}
                    onClick={() => setActiveId(it.id)}
                    className={cn(
                      "rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {it.title}
                  </button>
                );
              })}
            </div>
          )}

          {active && (
            <div
              role="tabpanel"
              id={`dashboard-panel-${active.id}`}
              aria-labelledby={`dashboard-tab-${active.id}`}
              className="flex flex-1 flex-col gap-2"
            >
              {/* El tablero se ve dentro de AROC y nada mas. Para las agencias
                  Holanda, el reinicio manual vive DENTRO de esta misma pantalla:
                  el boton alterna la vista sin abrir otra, y "Volver al tablero"
                  regresa. */}
              {reinicio && (
                <div className="flex justify-end">
                  {rebootView ? (
                    <button
                      type="button"
                      onClick={() => setShowReboot(false)}
                      className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      Volver al tablero
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowReboot(true)}
                      className="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      {reinicio.boton}
                    </button>
                  )}
                </div>
              )}
              {rebootView ? (
                <div className="flex-1 overflow-auto rounded-md border bg-card min-h-[calc(100vh-220px)]">
                  {reinicio?.paneles ? (
                    <RebootPanelesPage />
                  ) : (
                    <RebootAgenciasPage cliente={reinicio?.cliente} />
                  )}
                </div>
              ) : (
                <div className="relative flex-1 overflow-hidden rounded-md border bg-card min-h-[calc(100vh-180px)]">
                  {!iframeReady && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                      Cargando dashboard…
                    </div>
                  )}
                  <iframe
                    key={active.id}
                    src={active.url}
                    title={active.title}
                    className="block origin-top-left"
                    style={{
                      width: `${100 / DASHBOARD_ZOOM}%`,
                      height: `${100 / DASHBOARD_ZOOM}%`,
                      transform: `scale(${DASHBOARD_ZOOM})`,
                    }}
                    onLoad={() => setIframeReady(true)}
                    allow="fullscreen"
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
